import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { getProjectCsvMapping, updateProjectCsvMapping } from '@/features/projects/data/csvMapping';
import { queryKeys, useAccessToken } from '@/shared/hooks';
import type { ApiError } from '@/shared/data/client';
import type { CsvHeaderAliases, CsvStatusAliases, ProjectCsvMappingDto } from '@flowqa/shared';

type UpdateProjectCsvMappingInput = {
  projectId: string;
  headerAliases: CsvHeaderAliases;
  statusAliases: CsvStatusAliases;
};

export const useProjectCsvMappingQuery = (
  projectId: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<ProjectCsvMappingDto | null, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;

  return useQuery<ProjectCsvMappingDto | null, ApiError>({
    queryKey: queryKeys.projectCsvMapping(projectId, orgKey),
    enabled: Boolean(token && projectId) && (options?.enabled ?? true),
    queryFn: async (): Promise<ProjectCsvMappingDto | null> => {
      if (!projectId) return null;
      return await getProjectCsvMapping(projectId, orgForQuery, token);
    },
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
};

export const useUpdateProjectCsvMapping = (): UseMutationResult<
  ProjectCsvMappingDto,
  ApiError,
  UpdateProjectCsvMappingInput
> => {
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;

  return useMutation<ProjectCsvMappingDto, ApiError, UpdateProjectCsvMappingInput>({
    mutationFn: async (input: UpdateProjectCsvMappingInput): Promise<ProjectCsvMappingDto> => {
      return await updateProjectCsvMapping(input, orgForQuery);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.projectCsvMapping(updated.projectId, orgKey), updated);
    },
  });
};
